import {
  Box,
  FormControlLabel,
  Skeleton,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Popover } from "antd";
import dayjs from "dayjs";
import { useContext, useEffect, useState } from "react";
import dScribeJobsService from "../api/services/dScribeJobs-service";
import SharedTable from "../components/shared-table";
import { showErrorToast } from "../helpers/utils";
import unauth from "../images/unauth.png";
import { PermissionsContext } from "../permission-context";

function PhoneDictationStatus() {
  const [dictationDetails, setDictationDetails] = useState([]);
  const { loading, error, hasPermission } = useContext(PermissionsContext);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [showTableSkeleton, setSkeleton] = useState(true);
  const [hideDeveloperRecords, setHideDeveloperRecords] = useState(true);

  // Row Coloring Logic
  const getRowClassName = (record) => {
    const uploadCount = Number(record.pendingUploadCount) || 0;
    const dictationCount = Number(record.pendingDictationCount) || 0;

    if (uploadCount > 0) {
      return "row-red";
    }
    if (dictationCount > 0) {
      return "row-orange";
    }
    return "";
  };

  const fetchData = async () => {
    try {
      setSkeleton(true);
      const res = await dScribeJobsService.getVScribeAiDictStatus();

      // Extract latest record per user-deviceIdentifier and sort those records by pendingDictationCount descending
      const data = res.data;

      // ✅ declare it in the same scope
      const userlatestMap = new Map();

      for (const r of data) {
        const rDate = new Date(r.lastUpdatedDateTime);
        const key = `${r.userId}-${r.deviceIdentifier}`;

        const existing = userlatestMap.get(key);
        if (!existing || rDate > new Date(existing.lastUpdatedDateTime)) {
          userlatestMap.set(key, r);
        }
      }

      const sortedDictationDetails = Array.from(userlatestMap.values()).sort(
        (a, b) => {
          const aUpload = Number(a.pendingUploadCount) || 0;
          const bUpload = Number(b.pendingUploadCount) || 0;
          const aDict = Number(a.pendingDictationCount) || 0;
          const bDict = Number(b.pendingDictationCount) || 0;

          const aIsZero = aUpload === 0 && aDict === 0;
          const bIsZero = bUpload === 0 && bDict === 0;

          if (aIsZero && bIsZero) {
            return (
              new Date(b.lastUpdatedDateTime) - new Date(a.lastUpdatedDateTime)
            );
          }

          if (aUpload !== bUpload) return bUpload - aUpload;
          if (aDict !== bDict) return bDict - aDict;

          return (
            new Date(b.lastUpdatedDateTime) - new Date(a.lastUpdatedDateTime)
          );
        },
      );

      setDictationDetails(sortedDictationDetails);
    } catch (error) {
      console.log(error);
      showErrorToast("Failed to load data. Please try again.");
    } finally {
      setSkeleton(false);
    }
  };

  useEffect(() => {
    fetchData();

    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 1200);
    };
    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const columns = [
    {
      title: "User Id",
      dataIndex: "userId",
      fixed: "left",
      width: isSmallScreen ? 120 : 200,
      sorter: (a, b) => a.userId?.localeCompare(b.userId),
      render: (text) => {
        const contentStyle = {
          width: isSmallScreen ? "100px" : "180px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          cursor: isSmallScreen ? "pointer" : "default",
          color: isSmallScreen ? "#1890ff" : "inherit", // Blue only on mobile
        };

        return (
          <Popover
            content={
              <div
                style={{
                  maxWidth: "300px",
                  wordBreak: "break-all",
                  color: "white",
                }}
              >
                {text}
              </div>
            }
            trigger={isSmallScreen ? "click" : "hover"}
            color="rgba(0, 0, 0, 0.85)"
          >
            <div style={contentStyle}>{text}</div>
          </Popover>
        );
      },
    },
    {
      title: "Account",
      dataIndex: "accountId",
      sorter: (a, b) => a.accountId?.localeCompare(b.accountId),
      fixed: isSmallScreen ? false : "left",
      width: 100,
    },
    {
      title: "Platform",
      dataIndex: "platform",
      sorter: (a, b) => a.platform?.localeCompare(b.platform),
      fixed: isSmallScreen ? false : "left",
    },
    {
      title: "Storage Size (O)",
      dataIndex: "totalDictationSizeMB",
      width: 100,
      sorter: (a, b) => a.totalDictationSizeMB - b.totalDictationSizeMB,
      fixed: isSmallScreen ? false : "left",
      render: (text) => (
        <Tooltip title={`${text} MB`}>
          <span>{text}</span>
        </Tooltip>
      ),
    },
    {
      title: "Pending Upload Count",
      dataIndex: "pendingUploadCount",
      width: 100,
      sorter: (a, b) => a.pendingUploadCount - b.pendingUploadCount,
      fixed: isSmallScreen ? false : "left",
    },
    {
      title: "Pending Dictation Count",
      dataIndex: "pendingDictationCount",
      width: 100,
      sorter: (a, b) => a.pendingDictationCount - b.pendingDictationCount,
      fixed: isSmallScreen ? false : "left",
    },
    {
      title: "Last Updated Date Time",
      dataIndex: "lastUpdatedDateTime",
      render: (text) => dayjs(text).format("MM/DD/YYYY HH:mm:ss"),
      sorter: (a, b) =>
        new Date(a.lastUpdatedDateTime)?.valueOf() -
        new Date(b.lastUpdatedDateTime).valueOf(),
      fixed: isSmallScreen ? false : "left",
    },
    {
      title: "Device Identifier",
      dataIndex: "deviceIdentifier",
      sorter: (a, b) => a.deviceIdentifier?.localeCompare(b.deviceIdentifier),
    },
    {
      title: "Pending Upload List",
      dataIndex: "pendingUploadList",
      width: 300,
      sorter: (a, b) => a.pendingUploadList?.localeCompare(b.pendingUploadList),
      render: (text) => (
        <Tooltip title={text}>
          <div
            style={{
              maxHeight: "40px",
              overflowY: "auto",
              display: "block",
              whiteSpace: "normal",
              wordBreak: "normal",
              width: "100%",
            }}
          >
            {text ? text.replaceAll(",", ", ") : ""}
            {!text && "\u00A0"}
          </div>
        </Tooltip>
      ),
    },
    {
      title: "Pending Dictation List",
      dataIndex: "pendingDictationList",
      width: 300,
      sorter: (a, b) =>
        a.pendingDictationList?.localeCompare(b.pendingDictationList),
      render: (text) => (
        <Tooltip title={text}>
          <div
            style={{
              maxHeight: "40px",
              overflowY: "auto",
              display: "block",
              whiteSpace: "normal",
              wordBreak: "normal",
              width: "100%",
            }}
          >
            {text ? text.replaceAll(",", ", ") : ""}
            {!text && "\u00A0"}
          </div>
        </Tooltip>
      ),
    },
    {
      title: "Last Updated By",
      dataIndex: "lastUpdatedBy",
      sorter: (a, b) => a.lastUpdatedBy?.localeCompare(b.lastUpdatedBy),
    },
  ];

  const rows = dictationDetails.map((row, index) => ({
    id: index + 1,
    userId: row.userId,
    accountId: row.accountId,
    platform: row.platform,
    totalDictationSizeMB: row.totalDictationSizeMB,
    pendingUploadCount: row.pendingUploadCount,
    pendingUploadList: row.pendingUploadList,
    pendingDictationCount: row.pendingDictationCount,
    pendingDictationList: row.pendingDictationList,
    lastUpdatedBy: row.lastUpdatedBy,
    lastUpdatedDateTime: row.lastUpdatedDateTime,
    deviceIdentifier: row.deviceIdentifier,
  }));

  const filteredRows = hideDeveloperRecords
    ? rows.filter((r) => {
        const account = r.accountId?.toUpperCase() || "";
        return account !== "TD" && account !== "MHC";
      })
    : rows;

  const finalColumns =
    filteredRows.length === 0
      ? columns.map((col) => ({ ...col, fixed: undefined }))
      : columns;

  return (
    <>
      {!loading && !error && hasPermission("ViewVScribeAiPhoneDictStatus") && (
        <Box style={{ marginRight: "1%", marginLeft: "2%" }}>
          <Grid
            container
            alignItems="center"
            justifyContent="space-between"
            sx={{
              marginTop: 1.5,
              borderBottom: 1,
              borderColor: "#dfdfdf",
            }}
          >
            {/* Title Section */}
            <Grid>
              <Typography
                gutterBottom
                fontWeight="700"
                lineHeight={"31.25px"}
                fontSize={"24px"}
                fontStyle={"DM Sans"}
              >
                Client Dictation Status
              </Typography>
            </Grid>

            <Grid>
              <FormControlLabel
                control={
                  <Switch
                    id="hide-developer-records"
                    checked={hideDeveloperRecords}
                    onChange={(e) => setHideDeveloperRecords(e.target.checked)}
                    color="primary"
                    sx={{ transform: "scale(0.75)" }}
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    fontWeight="500"
                    fontStyle="DM Sans"
                    color="text.secondary"
                  >
                    Hide Developer Records
                  </Typography>
                }
              />
            </Grid>
          </Grid>

          <div
            style={{
              margin: "12px 0",
              fontFamily: "DM Sans",
              minHeight: "60vh",
            }}
          >
            {showTableSkeleton ? (
              <Skeleton
                variant="rectangular"
                width="100%"
                height="60vh"
                sx={{ borderRadius: "15px" }}
                animation="wave"
              />
            ) : (
              <SharedTable
                columns={finalColumns}
                dataSource={filteredRows}
                scroll={{ x: "max-content" }}
                rowClassName={getRowClassName}
                tableName="clientDictationStatus"
                rowKey="id"
              />
            )}
          </div>
        </Box>
      )}
      {!loading && !error && !hasPermission("ViewVScribeAiPhoneDictStatus") && (
        <Box
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "90vh",
          }}
        >
          <img
            src={unauth}
            alt="UnAuthorized"
            style={{ maxWidth: "100%", maxHeight: "100%" }}
          />
        </Box>
      )}
    </>
  );
}

export default PhoneDictationStatus;
